import type { LoginType } from '~~/common/config/LoginTypeConfig';
import { LT_ANON } from '~~/common/config/LoginTypeConfig';
import { asStringCookie } from '~~/common/utils/cookie';
import type { AuthGrantType } from '~~/common/config/AuthGrantType';
import { AGT_ANONYMOUS, AGT_REFRESH_TOKEN } from '~~/common/config/AuthGrantType';

interface GrantTypeTransformInput {
  body: {
    grant_type?: AuthGrantType
    refresh_token?: string
  } | null,
  cookies: ReadableCookies,
  clientId: string,
  clientSecret: string,
}

interface ReadableCookies {
  get(arg0: string): string | unknown,
}

interface WritableCookies {
  set(arg0: string, arg1: unknown, arg2: unknown): unknown,

  remove(arg0: string, arg1: unknown): unknown,
}

export function grantTypeTransform ({
  body,
  cookies,
  clientId,
  clientSecret,
}: GrantTypeTransformInput) {
  const newReqData = {
    ...(body || null),
    client_id: clientId,
    client_secret: clientSecret,
  };

  if (newReqData.grant_type === AGT_REFRESH_TOKEN) {
    if (cookies.get('login_type') !== LT_ANON && cookies.get('refresh_token')) {
      newReqData.refresh_token = asStringCookie(cookies.get('refresh_token'));
    } else {
      newReqData.grant_type = AGT_ANONYMOUS;
    }
  }

  return newReqData;
}

export function setLoginCookies ({
  cookies,
  body,
}: {
  cookies: WritableCookies,
  body: {
    login_type: LoginType | undefined,
    token_type: string | undefined,
    expires_in: number | undefined,
    access_token: string | undefined,
    refresh_token: string | undefined,
  },
}) {
  // NOTE: Checking !process.client works always, while checking
  // process.server does not. These two vars are not defined in case of
  // modules for seems. (eg proxy module)
  const isServer = !import.meta.client;
  [{
    name: 'token_type',
    value: body.token_type,
    opts: {
      path: '/',
      maxAge: body.expires_in,
      sameSite: 'lax',
    },
  }, {
    name: 'access_token',
    value: body.access_token,
    opts: {
      path: '/',
      maxAge: body.expires_in,
      sameSite: 'lax',
    },
  }, {
    name: 'refresh_token',
    value: body.refresh_token,
    opts: {
      path: '/',
      sameSite: 'lax',
      httpOnly: true,
    },
  }, {
    name: 'login_type',
    value: body.login_type,
    opts: {
      path: '/',
      maxAge: body.expires_in,
      sameSite: 'lax',
    },
  }].filter(c => isServer || !c?.opts?.httpOnly).forEach((c) => {
    if (c?.value === undefined) {
      cookies.remove(c.name, c.opts);
    } else {
      cookies.set(c.name, c.value, c.opts);
    }
  });
  // NOTE: Cookies set with cookies.set won't be available with cookies.get
  // until response is sent with the Set-Cookie header so the browser is
  // able to set them.
  return {
    isChangeAsync: isServer,
    isChangeRequiresServer: !isServer,
  };
}
